import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "flowbite-react";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

const MultiSelectPopover = ({
  label = "Selecionar",
  options = [], // [{id, name}]
  values = [], // [ids]
  onChange, // (ids) => void
  emptyLabel = "Pesquisar...",
  className = "",
  buttonClassName = "",
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return options;
    return options.filter((o) => (o.name || "").toLowerCase().includes(t));
  }, [q, options]);

  const toggle = (id) => {
    const set = new Set(values);
    set.has(id) ? set.delete(id) : set.add(id);
    onChange(Array.from(set));
  };

  const allSelected = values.length > 0 && values.length === options.length;

  const handleSelectAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(options.map((o) => o.id));
    }
  };

  useEffect(() => {
    if (!open) return;
    const onClickOut = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onClickOut);
    return () => window.removeEventListener("mousedown", onClickOut);
  }, [open]);

  return (
    <div className={classNames("relative", className)} ref={ref}>
      <Button
        onClick={() => setOpen((v) => !v)}
        color="light"
        size="xs"
        disabled={disabled}
        className={classNames(
          "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
          buttonClassName,
          disabled && "opacity-70 cursor-not-allowed"
        )}
      >
        {label}
        {values.length > 0 && (
          <span className="ml-2 text-[10px] bg-indigo-100 text-indigo-700 rounded px-1.5 py-0.5">
            {values.length}
          </span>
        )}
      </Button>

      {open && !disabled && (
        <div className="absolute z-50 mt-2 w-72 rounded-lg border border-gray-200 bg-white shadow-xl p-2">
          <div className="flex items-center gap-2 mb-2">
            <input
              type="text"
              placeholder={emptyLabel}
              className="w-full rounded border-gray-300 text-sm"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Button size="xs" color="light" onClick={handleSelectAll}>
              {allSelected ? "Limpar" : "Todos"}
            </Button>
          </div>

          <div className="max-h-64 overflow-auto pr-1">
            {filtered.length === 0 ? (
              <div className="text-xs text-gray-500 p-2">Sem resultados</div>
            ) : (
              <ul className="space-y-1">
                {filtered.map((o) => {
                  const checked = values.includes(o.id);
                  return (
                    <li
                      key={o.id}
                      className={classNames(
                        "flex items-center gap-2 rounded px-2 py-1 cursor-pointer",
                        checked ? "bg-indigo-50" : "hover:bg-gray-50"
                      )}
                      onClick={() => toggle(o.id)}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(o.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="text-sm text-gray-700 truncate">
                        {o.name}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-2">
            <Button size="xs" color="light" onClick={() => onChange([])}>
              Limpar seleção
            </Button>
            <Button size="xs" onClick={() => setOpen(false)}>
              Fechar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectPopover;
